import {
  EMPTY_EMPLOYEE_APP_EXPERIENCE_INSTANCE_PROGRESS,
  IEmployeeAppExperienceInstanceProgress,
  IEmployeeAppInboxMoment,
  IEmployeeAppInboxResponse,
  IEmployeeAppPinnedItemsResponse,
  maximizeInstanceProgress,
  MessageStatus,
  PARTICIPANT_ACTOR_KEY
} from '@dadohr/shared/domainTypes/experience';
import { PAEventTypeV2 } from '@dadohr/shared/domainTypes/productAnalytics';
import styled from '@emotion/styled';
import { Button, Typography } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import { MailIcon, UserIcon, UsersIcon } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import ReactHelmet from 'react-helmet';
import { Link } from 'react-router-dom';
import { ColoredChip } from '../../../../../../components/ColoredChip';
import { useDialogState } from '../../../../../../hooks/useDialogState';
import { useUnsafeTranslation } from '../../../../../../i18next';
import { Centered } from '../../../../../../layout/Centered';
import {
  FlexContainer,
  FlexContainerVertical
} from '../../../../../../layout/Flex';
import { useProductAnalyticsV2 } from '../../../../../features/Experience/services/productAnalytics';
import { useStandaloneAppThemeOverride } from '../../../../../hooks/useStandaloneAppContext';
import { ROUTES_V2 } from '../../../../../routes';
import { EmployeeAppCard } from '../../components/EmployeeAppCard';
import { EmployeeAppPageLoader } from '../../components/EmployeeAppLoader';
import {
  PAGE_PADDING,
  PageContainer,
  PageContainerInner
} from '../../components/PageFrame';
import { ProgressTracker } from '../../components/ProgressTracker';
import { SearchWithinInboxButton } from '../../components/Search/WithinInbox';
import { TopNavBarV2ForExperienceHub } from '../../components/TopNav';
import { useOwnArchivedInstances } from '../../services/archivedInstances';
import { usePinnedTaskGroups } from '../../services/pinnedItems';
import { useEmployeeAppInboxDataAndPinnedItemsAndUpdateDate } from '../../services/retrieval';
import { getInboxUrl, getIndividualInstanceUrl } from '../../services/routes';
import { MomentsTable } from '../Inbox/components/MomentsTable';
import { TaskGroupTable } from '../Inbox/components/TaskGroupTable';
import { WithTokenPill } from '../Inbox/components/WithTokenPill';
import { ArchivedOtherInstances } from './components/ArchivedOtherInstances';

const Title = styled(Typography)((p) => ({
  paddingBottom: p.theme.spacing(PAGE_PADDING - 1),
  borderBottom: p.theme.custom.border.standard,
  width: '100%'
}));

const Body = styled('div')((p) => ({
  padding: p.theme.spacing(PAGE_PADDING),
  maxWidth: '100%',
  width: '100%'
}));

const CardGrid = styled('div')((p) => ({
  display: 'grid',
  width: '100%',
  gridTemplateColumns: 'repeat(1, 1fr)',
  gridColumnGap: p.theme.spacing(3),
  gridRowGap: p.theme.spacing(2),
  alignItems: 'stretch',
  [p.theme.breakpoints.up('sm')]: {
    gridRowGap: p.theme.spacing(3),
    gridTemplateColumns: 'repeat(2, 1fr)'
  },
  [p.theme.breakpoints.up('md')]: {
    gridTemplateColumns: 'repeat(3, 1fr)'
  },
  [p.theme.breakpoints.up('lg')]: {
    gridTemplateColumns: 'repeat(4, 1fr)'
  },
  [p.theme.breakpoints.up('xl')]: {
    gridTemplateColumns: 'repeat(5, 1fr)'
  },
  '> *': {
    minHeight: p.theme.spacing(15)
  }
}));

const FullGridColumn = styled('div')((p) => ({
  width: '100%',
  gridColumn: 'span 3'
}));

const IntanceCount = ({ count }: { count: number }) => {
  return (
    <FlexContainer>
      {count > 1 ? <UsersIcon size={20} /> : <UserIcon size={20} />}
      <ColoredChip color="gray" mode="light">
        {count}
      </ColoredChip>
    </FlexContainer>
  );
};

const Group = ({
  title,
  cards
}: {
  title?: React.ReactNode;
  cards: React.ReactNode[];
}) => {
  return (
    <FlexContainerVertical fullWidth spacing={3}>
      {title && <Title variant="h3">{title}</Title>}
      <CardGrid>{cards}</CardGrid>
    </FlexContainerVertical>
  );
};

const MessageCount = ({ count }: { count: number }) => {
  const { t } = useUnsafeTranslation();
  return (
    <Typography variant="body2" color="textSecondary">
      <FlexContainer alignItems="center" spacing={1}>
        <MailIcon size={18} />
        {t('messagesWithCount', { count })}
      </FlexContainer>
    </Typography>
  );
};

const OwnInstanceCard = ({
  templateId,
  templateTitle,
  progress,
  msgCount
}: {
  templateId: string;
  templateTitle: string;
  progress: IEmployeeAppExperienceInstanceProgress;
  msgCount: number;
}) => {
  const hasNoTasks = progress.counts.total === 0;
  return (
    <EmployeeAppCard
      key={templateId}
      hoverEffect
      cursorPointer
      fullHeight
      padding={2.5}
    >
      <FlexContainerVertical
        fullWidth
        fullHeight
        justifyContent="space-between"
        spacing={1}
      >
        <Typography variant="h5">
          <WithTokenPill d={templateTitle} />
        </Typography>
        {hasNoTasks ? (
          <MessageCount count={msgCount} />
        ) : (
          <ProgressTracker d={progress} noTitle stretch />
        )}
      </FlexContainerVertical>
    </EmployeeAppCard>
  );
};

const OwnInstanceCardSkeleton = () => {
  return (
    <EmployeeAppCard fullHeight padding={2.5}>
      <FlexContainerVertical
        fullWidth
        fullHeight
        justifyContent="space-between"
        spacing={1}
      >
        <Typography variant="h5">
          <Skeleton variant="text" width={200} />
        </Typography>
        <FlexContainer fullWidth justifyContent="space-between">
          <Skeleton variant="text" width={150} />
          <Skeleton variant="text" width={30} />
        </FlexContainer>
      </FlexContainerVertical>
    </EmployeeAppCard>
  );
};

const OwnArchivedInstances = ({
  userAccessToken
}: {
  userAccessToken: string;
}) => {
  const [ds] = useOwnArchivedInstances(userAccessToken);
  const { trackEventV2 } = useProductAnalyticsV2(userAccessToken);
  if (!ds) {
    return (
      <Group
        cards={[<OwnInstanceCardSkeleton />, <OwnInstanceCardSkeleton />]}
      />
    );
  }

  return (
    <Group
      cards={ds.map((x) => (
        <Link
          key={x.templateId}
          to={getIndividualInstanceUrl(userAccessToken, x.instanceAccessToken)}
          onClick={() => {
            trackEventV2({
              eventType: PAEventTypeV2.OTHER,
              name: 'Dashboard Experience Card Clicked',
              Explanation: 'User clicked on a card on the dashboard',
              'Role in Experience': 'Participant',
              Archived: 'Yes'
            });
          }}
        >
          <OwnInstanceCard
            templateId={x.templateId}
            templateTitle={x.templateTitle}
            progress={maximizeInstanceProgress(x.progress)}
            msgCount={x.msgCount}
          />
        </Link>
      ))}
    />
  );
};

const MomentsSection = ({
  userAccessToken,
  moments,
  hasArchivedMoments
}: {
  userAccessToken: string;
  moments: IEmployeeAppInboxMoment[];
  hasArchivedMoments: boolean;
}) => {
  const { t } = useUnsafeTranslation();
  const activeMoments = useMemo(() => {
    return moments.filter((m) => m.markedAsRead === null);
  }, [moments]);
  return (
    <FlexContainerVertical fullWidth spacing={2}>
      <Typography variant="h3">{t('News')}</Typography>
      <FlexContainerVertical fullWidth spacing={1}>
        {activeMoments.length > 0 && (
          <CardGrid>
            <FullGridColumn>
              <MomentsTable
                moments={activeMoments}
                userAccessToken={userAccessToken}
                returnTo={ROUTES_V2.dashboard.url(userAccessToken)}
              />
            </FullGridColumn>
          </CardGrid>
        )}
        {hasArchivedMoments && (
          <Link
            to={ROUTES_V2.inbox.url(userAccessToken, { g: 'moments-archived' })}
          >
            <Button
              style={{
                fontWeight: 400
              }}
            >
              {t('Show archived news')}
            </Button>
          </Link>
        )}
      </FlexContainerVertical>
    </FlexContainerVertical>
  );
};

const EmployeeAppPageDashboardWithData = ({
  data,
  pinnedItems
}: {
  data: IEmployeeAppInboxResponse;
  pinnedItems: IEmployeeAppPinnedItemsResponse['items'];
}) => {
  const archiveDialog = useDialogState();
  useStandaloneAppThemeOverride(data.org.experienceTheming || null);
  const { t } = useUnsafeTranslation();
  const { userAccessToken } = data;
  const { trackEventV2 } = useProductAnalyticsV2(userAccessToken);

  useEffect(() => {
    trackEventV2({
      eventType: PAEventTypeV2.OTHER,
      name: 'Dashboard Page Loaded',
      Explanation: 'A user opened the dashboard of the employee app'
    });
  }, [trackEventV2]);

  const { myInstances, otherInstances, moments } = useMemo(() => {
    const my: {
      [templateId: string]: {
        templateId: string;
        templateTitle: string;
        instanceAccessToken: string;
        progress: IEmployeeAppExperienceInstanceProgress;
        msgCount: number;
      };
    } = {};
    const other: {
      [templateId: string]: {
        templateId: string;
        templateTitle: string;
        instances: Set<string>;
      };
    } = {};

    const n = Date.now();

    [...data.tasks, ...data.messages].forEach((x) => {
      if (x.actorKey === PARTICIPANT_ACTOR_KEY) {
        if (!my[x.templateId]) {
          my[x.templateId] = {
            templateId: x.templateId,
            templateTitle: x.templateTitle,
            instanceAccessToken: x.instanceAccessToken,
            progress:
              data.additionalDataForOwnInstances[x.instanceAccessToken]
                ?.progress || EMPTY_EMPLOYEE_APP_EXPERIENCE_INSTANCE_PROGRESS(),
            msgCount: 0
          };
        }
      } else {
        other[x.templateId] = other[x.templateId] || {
          templateId: x.templateId,
          templateTitle: x.templateTitle,
          instances: new Set()
        };
        other[x.templateId].instances.add(x.instanceAccessToken);
      }
    });

    data.messages.forEach((x) => {
      if (x.actorKey === PARTICIPANT_ACTOR_KEY) {
        if (x.sendAt.toMillis() < n) {
          if (my[x.templateId] && x.status !== MessageStatus.SKIPPED) {
            // should always be true
            my[x.templateId].msgCount++;
          }
        }
      }
    });

    return {
      myInstances: Object.values(my),
      otherInstances: Object.values(other).map((x) => ({
        templateId: x.templateId,
        templateTitle: x.templateTitle,
        count: x.instances.size
      })),
      moments: data.moments
    };
  }, [data]);

  const pinnedTaskGroups = usePinnedTaskGroups(data, pinnedItems);

  const [showOwnArchivedInstances, setShowOwnArchivedInstances] = useState(
    false
  );

  const isFullyEmpty =
    !myInstances.length &&
    !otherInstances.length &&
    !pinnedTaskGroups.length &&
    !data.hasArchivedOwnInstances &&
    !data.hasArchivedOtherInstances &&
    !moments.length &&
    !data.hasArchivedMoments;

  return (
    <>
      <ReactHelmet>
        <title>{t('Experiences')}</title>
      </ReactHelmet>

      <PageContainer>
        <PageContainerInner>
          <TopNavBarV2ForExperienceHub
            data={data}
            searchComponent={<SearchWithinInboxButton data={data} />}
          />
          <Body>
            {isFullyEmpty && (
              <>
                <Centered height={300}>
                  {t(`You haven't yet been involved in any Dado experiences.`)}
                </Centered>
              </>
            )}
            <FlexContainerVertical fullWidth spacing={7}>
              {!!myInstances.length && (
                <FlexContainerVertical fullWidth spacing={3}>
                  <Group
                    title={t('My experiences')}
                    cards={myInstances.map((x) => {
                      return (
                        <Link
                          key={x.templateId}
                          to={getIndividualInstanceUrl(
                            userAccessToken,
                            x.instanceAccessToken
                          )}
                          onClick={() => {
                            trackEventV2({
                              eventType: PAEventTypeV2.OTHER,
                              name: 'Dashboard Experience Card Clicked',
                              Explanation:
                                'User clicked on a card on the dashboard',
                              'Role in Experience': 'Participant',
                              Archived: 'No'
                            });
                          }}
                        >
                          <OwnInstanceCard
                            templateId={x.templateId}
                            templateTitle={x.templateTitle}
                            progress={x.progress}
                            msgCount={x.msgCount}
                          />
                        </Link>
                      );
                    })}
                  />
                  {data.hasArchivedOwnInstances && (
                    <Button
                      onClick={() => setShowOwnArchivedInstances((x) => !x)}
                      style={{
                        fontWeight: 400
                      }}
                    >
                      {showOwnArchivedInstances
                        ? t('Hide archived experiences')
                        : t('Show archived experiences')}
                    </Button>
                  )}
                  {data.hasArchivedOwnInstances && showOwnArchivedInstances && (
                    <OwnArchivedInstances userAccessToken={userAccessToken} />
                  )}
                </FlexContainerVertical>
              )}

              {!myInstances.length && data.hasArchivedOwnInstances && (
                <FlexContainerVertical fullWidth spacing={3}>
                  <Title variant="h3">{t('My experiences')}</Title>
                  <div>{t('You have no active experiences')}</div>
                  {data.hasArchivedOwnInstances && (
                    <Button
                      onClick={() => setShowOwnArchivedInstances((x) => !x)}
                      style={{
                        fontWeight: 400
                      }}
                    >
                      {showOwnArchivedInstances
                        ? t('Hide archived experiences')
                        : t('Show archived experiences')}
                    </Button>
                  )}
                  {data.hasArchivedOwnInstances && showOwnArchivedInstances && (
                    <OwnArchivedInstances userAccessToken={userAccessToken} />
                  )}
                </FlexContainerVertical>
              )}
              {!!otherInstances.length && (
                <FlexContainerVertical fullWidth spacing={3}>
                  <Group
                    title={t(`Experiences I'm part of`)}
                    cards={otherInstances.map((x) => {
                      return (
                        <Link
                          key={x.templateId}
                          to={getInboxUrl(userAccessToken, {
                            t: [x.templateId]
                          })}
                          onClick={() => {
                            trackEventV2({
                              eventType: PAEventTypeV2.OTHER,
                              name: 'Dashboard Experience Card Clicked',
                              Explanation:
                                'User clicked on a card on the dashboard',
                              'Role in Experience': 'Supporting Actor',
                              Archived: 'No'
                            });
                          }}
                        >
                          <EmployeeAppCard
                            key={x.templateId}
                            hoverEffect
                            cursorPointer
                            fullHeight
                            padding={2.5}
                          >
                            <FlexContainer
                              fullWidth
                              justifyContent="space-between"
                            >
                              <Typography variant="h5">
                                <WithTokenPill d={x.templateTitle} />
                              </Typography>
                              <IntanceCount count={x.count} />
                            </FlexContainer>
                          </EmployeeAppCard>
                        </Link>
                      );
                    })}
                  />
                  {data.hasArchivedOtherInstances && (
                    <Button
                      onClick={() => archiveDialog.openDialog()}
                      style={{
                        fontWeight: 400
                      }}
                    >
                      {t('Show archived experiences')}
                    </Button>
                  )}
                </FlexContainerVertical>
              )}

              {!otherInstances.length && data.hasArchivedOtherInstances && (
                <FlexContainerVertical fullWidth spacing={3}>
                  <Title>{t(`Experiences I'm part of`)}</Title>
                  <div>{t('You are not part of any active experiences')}</div>
                  <Button
                    onClick={() => archiveDialog.openDialog()}
                    style={{
                      fontWeight: 400
                    }}
                  >
                    {t('Show archived experiences')}
                  </Button>
                </FlexContainerVertical>
              )}

              {!!pinnedTaskGroups.length && !isFullyEmpty && (
                <FlexContainerVertical fullWidth spacing={2}>
                  <Typography variant="h3">{t('My pinned tasks')}</Typography>
                  <CardGrid>
                    <div style={{ width: '100%', gridColumn: 'span 3' }}>
                      <TaskGroupTable
                        groups={pinnedTaskGroups}
                        mode="withSupportingActor"
                        userAccessToken={userAccessToken}
                        returnTo={ROUTES_V2.dashboard.url(userAccessToken)}
                        highlightOverdue
                        includePinColumn
                      />
                    </div>
                  </CardGrid>
                </FlexContainerVertical>
              )}

              {(!!moments.length || data.hasArchivedMoments) &&
                !isFullyEmpty && (
                  <MomentsSection
                    userAccessToken={userAccessToken}
                    moments={moments}
                    hasArchivedMoments={
                      data.hasArchivedMoments ||
                      data.moments.filter((m) => m.markedAsRead).length > 0
                    }
                  />
                )}
            </FlexContainerVertical>
          </Body>
        </PageContainerInner>
      </PageContainer>
      {archiveDialog.dialogOpen && (
        <ArchivedOtherInstances
          open={archiveDialog.dialogOpen}
          onClose={archiveDialog.closeDialog}
          userAccessToken={userAccessToken}
        />
      )}
    </>
  );
};

export const EmployeeAppPageDashboard = ({
  userAccessToken,
  v
}: {
  userAccessToken: string;
  v: number;
}) => {
  const [data] = useEmployeeAppInboxDataAndPinnedItemsAndUpdateDate(
    userAccessToken
  );
  if (data) {
    return (
      <EmployeeAppPageDashboardWithData
        data={data.inbox}
        pinnedItems={data.pinnedItems}
      />
    );
  }
  return <EmployeeAppPageLoader />;
};
